import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Route, Router, Routes, UrlSegment} from '@angular/router';
import {FrontLayoutComponent} from '../components/layouts/front-layout/front-layout.component';
import {PageComponent} from '../components/web/page-component/page.component';
import {lastValueFrom, timer} from 'rxjs';
import {NotFoundComponent} from '../../views/public/not-found/not-found.component';
import {GalleryComponent} from '../components/web/gallery-component/gallery.component';

@Injectable()
export class RouterConfigService {

    private _configData?: Routes
    private _promise?: Promise<any> | null
    private _promiseDone = false

    get configData(): any {
        return this._configData
    }

    constructor(private http: HttpClient,
                private router: Router) {
        // check age of loaded config - 1 hour
        timer(3600000, 3600000).subscribe(response => {
            this.renewRoutes(router, '').then()
        })
    }

    renewRoutes(router: Router, modulePath: string) {
        // force to load new routes
        this._promiseDone = false
        this._promise = null
        return this.addDynamicRoutes(router, modulePath)
    }

    addDynamicRoutes(router: Router, modulePath: string):
        Promise<Route[]> {

        return new Promise((resolve, reject) => {
                this.loadConfig().then((dynamicRoutes) => {
                    let configIsChanged = false;

                    router.config.forEach(root => {
                        // check/set child routes
                        if ((root as any)._loadedRoutes && (root as any)._loadedRoutes.length > 0) {
                            const foundChild: any =
                                (root as any)._loadedRoutes[0]?.children?.find((child: any) => {
                                    return child.path === modulePath
                                });
                            if (foundChild && (foundChild._loadedRoutes?.length || 0) > 0) {
                                foundChild._loadedRoutes.forEach((childRoute: any) => {
                                    if (childRoute.data && childRoute.data.addDynamicChild) {
                                        childRoute.children = dynamicRoutes[0].children
                                        router.resetConfig(router.config)
                                        configIsChanged = true
                                    }
                                })
                            }
                            // root routes
                        } else if (root.path === '') {
                            // updates of main routes (pages, products, ...)
                            root.children = dynamicRoutes[0].children
                            router.resetConfig(router.config)
                            configIsChanged = true
                        }
                    })
                    // first set of root dynamic routes
                    if (!configIsChanged && modulePath === '') {
                        router.config.splice(0, 0, dynamicRoutes[0])
                    }

                    if (configIsChanged) {
                        resolve(router.config);
                    }
                    resolve([]);
                })
            }
        )
    }

    addOnlySecuredRoute(router: Router) {
        return new Promise((resolve, reject) => {
            const frontSecuredRoutes: Route = {
                path: '',
                pathMatch: 'full',
                redirectTo: '/sec',
            }
            router.config.splice(0, 0, frontSecuredRoutes)
            router.resetConfig(router.config)
            resolve(router.config)
        })
    }

    loadConfig(isBackend = false): Promise<any> {
        const url = '/api/v1/settings/routing'

        if (this._promiseDone) {
            return Promise.resolve(this._configData)
        }

        if (this._promise != null) {
            return this._promise
        }

        this._promise =
            lastValueFrom(this.http.get(url))
                .then((data: any) => {
                    const frontRoutes = {
                        path: '',
                        component: FrontLayoutComponent,
                        children: [] as Route[],
                    }

                    const component: any = {}
                    component['page'] = PageComponent
                    component['gallery'] = GalleryComponent

                    data.reduce((routes: any, module: any) => {
                        routes.push({
                            component: component[module.type],
                            pathMatch: 'full',
                            matcher: (link: UrlSegment[]) => {

                                return link.length === 1 && link[0].path.match(new RegExp('^(' + module.alias + ')$', 'gmy')) ? {
                                    consumed: link,
                                    posParams: {
                                        alias: new UrlSegment(link[0].path, {})
                                    }
                                } : null
                            }
                        })
                        return routes
                    }, frontRoutes.children)

                    // basic front routes
                    frontRoutes.children.push({
                        path: '',
                        pathMatch: 'full',
                        component: PageComponent
                    })

                    frontRoutes.children.push({
                        path: '404',
                        pathMatch: 'full',
                        component: NotFoundComponent,
                        data: {title: 'page-not-found'}
                    })

                    this._configData = [frontRoutes]
                    this._promiseDone = true
                    return this._configData
                })
                .catch((err: any) => {
                    // console.error('err')
                    this._promiseDone = true
                    return Promise.resolve()
                })
        return this._promise
    }
}
